import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, ProgressBar, Row } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from './useStudyYearSelector';

function scoreVariant(pts) {
  const n = Number(pts);
  if (n >= 80) return 'success';
  if (n >= 60) return 'warning';
  return 'danger';
}

function scoreLabel(pts) {
  const n = Number(pts);
  if (n >= 80) return 'ดี';
  if (n >= 60) return 'ควรปรับปรุง';
  return 'เสี่ยง';
}

export default function DisciplineConduct() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [keyword,       setKeyword]       = useState('');
  const [students,      setStudents]      = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rules,         setRules]         = useState([]);
  const [form,          setForm]          = useState({ ruleId: '', deductPoints: '', note: '' });
  const [saving,        setSaving]        = useState(false);
  const [message,       setMessage]       = useState('');
  const [error,         setError]         = useState('');
  const [mobileView,    setMobileView]    = useState('list');

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    axiosInstance.get('/api/discipline/conduct-rules')
      .then(res => setRules(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelected(null);
    setDetail(null);
    setMobileView('list');
  }, [yearId]);

  const search = useCallback((e) => {
    e?.preventDefault();
    if (!yearId) return;
    setError('');
    axiosInstance
      .get(`/api/discipline/conduct/students?yearId=${yearId}&keyword=${encodeURIComponent(keyword.trim())}`)
      .then(res => setStudents(res.data || []))
      .catch(() => setError('ไม่สามารถค้นหานักเรียนได้'));
  }, [yearId, keyword]);

  useEffect(() => { if (yearId) search(); }, [yearId, search]);

  const loadDetail = useCallback((student) => {
    setSelected(student);
    setDetail(null);
    setDetailLoading(true);
    setMobileView('detail');
    setMessage('');
    setError('');
    axiosInstance
      .get(`/api/discipline/conduct/${student.st_id}/${yearId}`)
      .then(res => setDetail(res.data))
      .catch(() => setError('ไม่สามารถโหลดประวัติความประพฤติได้'))
      .finally(() => setDetailLoading(false));
  }, [yearId]);

  const selectedRule = useMemo(
    () => rules.find(r => String(r.rule_id) === String(form.ruleId)),
    [rules, form.ruleId]
  );

  useEffect(() => {
    if (selectedRule) setForm(prev => ({ ...prev, deductPoints: selectedRule.default_points }));
  }, [selectedRule]);

  const saveRecord = (e) => {
    e.preventDefault();
    if (!selected || !detail?.student) return;
    setSaving(true);
    setError('');
    setMessage('');
    axiosInstance
      .post('/api/discipline/conduct-records', {
        studentId:    selected.st_id,
        studyId:      detail.student.study_id,
        yearId,
        ruleId:       form.ruleId,
        deductPoints: Number(form.deductPoints),
        note:         form.note,
      })
      .then(() => {
        setMessage('บันทึกการตัดคะแนนเรียบร้อยแล้ว');
        setForm({ ruleId: '', deductPoints: '', note: '' });
        loadDetail(selected);
        search();
      })
      .catch(err => setError(err.response?.data?.message || 'ไม่สามารถบันทึกได้'))
      .finally(() => setSaving(false));
  };

  const summary  = detail?.summary || { deducted_points: 0, remaining_points: 100 };
  const variant  = scoreVariant(summary.remaining_points);

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* page header */}
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">คะแนนความประพฤติ</li>
            </ol>
            <h4 className="main-title mb-0">จัดการคะแนนความประพฤติ</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(y => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* alerts */}
        {message && (
          <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ✓ {message}
            <button type="button" className="btn-close ms-auto" onClick={() => setMessage('')} />
          </div>
        )}
        {error && (
          <div className="alert alert-danger alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ⚠ {error}
            <button type="button" className="btn-close ms-auto" onClick={() => setError('')} />
          </div>
        )}

        {/* main row */}
        <Row className="g-3">

          {/* student list — hidden on mobile when detail is open */}
          <Col xl={4} className={mobileView === 'detail' ? 'd-none d-xl-block' : ''}>
            <Card className="card-one mb-3">
              <Card.Body>
                <Form onSubmit={search}>
                  <InputGroup>
                    <Form.Control
                      value={keyword}
                      onChange={e => setKeyword(e.target.value)}
                      placeholder="เลขประจำตัว หรือชื่อนักเรียน"
                      style={{ fontSize: 16 }}
                    />
                    <Button type="submit" variant="primary">ค้นหา</Button>
                  </InputGroup>
                </Form>
              </Card.Body>
            
              <Card.Header className="d-flex align-items-center justify-content-between py-2">
                <Card.Title as="h6" className="mb-0">รายชื่อนักเรียน</Card.Title>
                {students.length > 0 && (
                  <Badge bg="secondary" className="fw-normal">{students.length} คน</Badge>
                )}
              </Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                {students.length === 0 ? (
                  <div className="text-center text-secondary py-5">
                    <div style={{ fontSize: 34, opacity: 0.25 }}>🔍</div>
                    ไม่พบนักเรียน
                  </div>
                ) : students.map(st => {
                  const isSelected = selected?.st_id === st.st_id;
                  const pts = Number(st.remaining_points);
                  return (
                    <button
                      key={st.st_id}
                      type="button"
                      onClick={() => loadDetail(st)}
                      style={{ minHeight: 60, cursor: 'pointer', transition: 'background .15s' }}
                      className={[
                        'd-flex align-items-center gap-3 w-100 text-start border-0 border-bottom px-3 py-3',
                        isSelected ? 'bg-primary text-white' : 'bg-transparent text-body',
                      ].join(' ')}
                    >
                      <div
                        style={{
                          width: 42, height: 42, fontSize: 13, flexShrink: 0,
                          border: `2px solid ${isSelected ? 'white' : 'currentColor'}`,
                        }}
                        className={[
                          'd-flex align-items-center justify-content-center rounded-circle fw-bold',
                          isSelected ? 'bg-white text-primary' : `text-${scoreVariant(pts)}`,
                        ].join(' ')}
                      >
                        {pts}
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-semibold text-truncate">{st.st_name}</div>
                        <div className={`fs-xs text-truncate ${isSelected ? 'text-white opacity-75' : 'text-secondary'}`}>
                          {st.st_idnumber} · ม.{st.c_id}/{st.r_no} · {st.p_code}
                        </div>
                      </div>
                      <span className={isSelected ? 'text-white' : 'text-secondary'} style={{ flexShrink: 0 }}>›</span>
                    </button>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>

          {/* detail — hidden on mobile when list is open */}
          <Col xl={8} className={mobileView === 'list' ? 'd-none d-xl-block' : ''}>

            <Button
              variant="outline-secondary"
              size="sm"
              className="d-xl-none mb-3"
              onClick={() => setMobileView('list')}
            >
              ← กลับรายชื่อนักเรียน
            </Button>

            {/* loading spinner */}
            {detailLoading && (
              <Card className="card-one">
                <Card.Body className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <div className="text-secondary">กำลังโหลดข้อมูล…</div>
                </Card.Body>
              </Card>
            )}

            {/* empty state */}
            {!detailLoading && !detail && (
              <Card className="card-one">
                <Card.Body className="text-center text-secondary py-5 px-4">
                  <div style={{ fontSize: 40, opacity: 0.2 }}>👤</div>
                  <div className="mt-2">เลือกนักเรียนจากรายชื่อด้านซ้าย<br />เพื่อดูคะแนนและบันทึกการตัดคะแนน</div>
                </Card.Body>
              </Card>
            )}

            {/* student content */}
            {!detailLoading && detail && (
              <>
                {/* score card */}
                <Card className="card-one mb-3">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-3">
                      <div className="flex-grow-1">
                        <h5 className="mb-1">{detail.student.st_name}</h5>
                        <div className="text-secondary fs-sm">
                          {detail.student.st_idnumber} · ม.{detail.student.c_id}/{detail.student.r_no} · {detail.student.p_code}
                        </div>
                      </div>
                      <div className="text-center" style={{ minWidth: 80, flexShrink: 0 }}>
                        <div className={`fw-bold lh-1 text-${variant}`} style={{ fontSize: 40 }}>
                          {summary.remaining_points}
                        </div>
                        <div className="text-secondary" style={{ fontSize: 11 }}>/ 100 คะแนน</div>
                        <Badge bg={variant} className="mt-1">{scoreLabel(summary.remaining_points)}</Badge>
                      </div>
                    </div>
                    <ProgressBar
                      now={Math.max(0, summary.remaining_points)}
                      variant={variant}
                      className="mt-3 rounded-pill"
                      style={{ height: 8 }}
                    />
                    <div className="d-flex justify-content-between mt-1" style={{ fontSize: 11 }}>
                      <span className="text-secondary">ตัดไปแล้ว {summary.deducted_points} คะแนน</span>
                      <span className={`fw-semibold text-${variant}`}>คงเหลือ {summary.remaining_points} คะแนน</span>
                    </div>
                  </Card.Body>

                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">บันทึกตัดคะแนน</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    {rules.length === 0 ? (
                      <div className="alert alert-warning mb-0">
                        ยังไม่มีรายการความผิด —{' '}
                        <Link to="/discipline/conduct-rules">ไปเพิ่มรายการความผิด</Link>
                      </div>
                    ) : (
                      <Form onSubmit={saveRecord}>
                        <Row className="g-3">
                          <Col xs={12} md={5}>
                            <Form.Label className="fw-semibold mb-1">
                              รายการความผิด <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                              value={form.ruleId}
                              onChange={e => setForm({ ...form, ruleId: e.target.value })}
                              required
                              style={{ fontSize: 16 }}
                            >
                              <option value="">— เลือกรายการความผิด —</option>
                              {rules.map(r => (
                                <option key={r.rule_id} value={r.rule_id}>
                                  {r.rule_name} ({r.default_points} คะแนน)
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col xs={5} md={2}>
                            <Form.Label className="fw-semibold mb-1">
                              คะแนนที่ตัด <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="number" min="1" max="100"
                              value={form.deductPoints}
                              onChange={e => setForm({ ...form, deductPoints: e.target.value })}
                              required
                              style={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col xs={7} md={5}>
                            <Form.Label className="fw-semibold mb-1">หมายเหตุ</Form.Label>
                            <Form.Control
                              value={form.note}
                              placeholder="รายละเอียดเพิ่มเติม"
                              onChange={e => setForm({ ...form, note: e.target.value })}
                              style={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col xs={12}>
                            <Button
                              type="submit" variant="danger"
                              className="w-100 py-2 fw-semibold"
                              disabled={saving}
                              style={{ fontSize: 16 }}
                            >
                              {saving
                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" />กำลังบันทึก…</>
                                : 'บันทึกการตัดคะแนน'}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}
          </Col>
        </Row>

        {/* history — full width below both columns */}
        {detail && !detailLoading && (
          <Row className="g-3 mt-0">
            <Col xs={12} className={mobileView === 'list' ? 'd-none d-xl-block' : ''}>
              <Card className="card-one">
                <Card.Header className="d-flex align-items-center">
                  <Card.Title as="h6" className="mb-0">
                    ประวัติการตัดคะแนน — {detail.student.st_name}
                  </Card.Title>
                  <Badge bg="danger" className="ms-auto fw-normal">
                    รวมตัด {summary.deducted_points} คะแนน
                  </Badge>
                </Card.Header>
                <Card.Body className="p-0">

                  {/* desktop table */}
                  <div className="d-none d-md-block" style={{ overflowX: 'auto' }}>
                    <table className="table table-hover mb-0">
                      <thead className="table-secondary">
                        <tr>
                          <th>วันที่</th>
                          <th>รายการ</th>
                          <th>หมายเหตุ</th>
                          <th>ผู้บันทึก</th>
                          <th className="text-center" style={{ width: 90 }}>คะแนน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.records.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-secondary py-4">
                              ยังไม่มีประวัติการตัดคะแนน
                            </td>
                          </tr>
                        ) : detail.records.map(rec => (
                          <tr key={rec.record_id}>
                            <td className="text-nowrap">{new Date(rec.created_at).toLocaleDateString('th-TH')}</td>
                            <td>{rec.rule_name_snapshot}</td>
                            <td className="text-secondary">{rec.note || '-'}</td>
                            <td className="text-secondary">{rec.created_by_name || '-'}</td>
                            <td className="text-center text-danger fw-semibold">-{rec.deduct_points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* mobile card rows */}
                  <div className="d-md-none">
                    {detail.records.length === 0 ? (
                      <div className="text-center text-secondary py-4">ยังไม่มีประวัติการตัดคะแนน</div>
                    ) : detail.records.map(rec => (
                      <div key={rec.record_id} className="d-flex align-items-start gap-3 px-3 py-3 border-bottom">
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{rec.rule_name_snapshot}</div>
                          <div className="text-secondary mt-1" style={{ fontSize: 12 }}>
                            {new Date(rec.created_at).toLocaleDateString('th-TH')}
                            {rec.created_by_name ? ` · ${rec.created_by_name}` : ''}
                          </div>
                          {rec.note && (
                            <div className="text-secondary" style={{ fontSize: 12 }}>หมายเหตุ: {rec.note}</div>
                          )}
                        </div>
                        <Badge bg="danger" className="py-2 px-2" style={{ flexShrink: 0 }}>
                          -{rec.deduct_points}
                        </Badge>
                      </div>
                    ))}
                  </div>

                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        <Footer />
      </div>
    </>
  );
}
